'use client'
import React, { FC, useState } from 'react'
import {
  HomePageControlIconName,
  IHomePageTemplateFeaturesBlock,
} from '@/types/firebase'
import Link from 'next/link'
import Image from 'next/image'
import { ICommonIconProps } from '@/types/shared'
import { ArrowRadarIcon } from '@/components/icons/arrow-radar-icon'
import { CheckRoundedIcon } from '@/components/icons/check-rounded-icon'

const homepageTemplatesIconsObj: {
  [key in HomePageControlIconName]: React.FC<ICommonIconProps>
} = {
  [HomePageControlIconName.ArrowRadar]: ArrowRadarIcon,
  [HomePageControlIconName.CheckRounded]: CheckRoundedIcon,
  // TODO: next icons should be changed
  [HomePageControlIconName.Engineering]: ArrowRadarIcon,
  [HomePageControlIconName.Mathematics]: ArrowRadarIcon,
  [HomePageControlIconName.Science]: ArrowRadarIcon,
  [HomePageControlIconName.Technology]: ArrowRadarIcon,
}

interface Props {
  customContent: IHomePageTemplateFeaturesBlock
}

export const CommonFeaturesBlock: FC<Props> = ({
  customContent: { title, description, featuresTitle, features, link, image },
}) => {
  const [hoveredElementId, setHoveredElementId] = useState<string>()

  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
      {/* Content Block */}
      <div className="w-full flex flex-col-reverse lg:flex-row lg:items-center gap-8 lg:gap-1">
        {/* Image Container */}
        <div className="relative w-full lg:w-7/12 sm:min-h-[400px] lg:min-h-[735px] flex justify-center">
          <div className="relative w-full h-0 pb-[100%]">
            <Image
              src={image.src}
              alt="features-bg-img"
              className="absolute top-0 lg:left-[-40px] w-full h-full object-contain"
              fill
              priority
            />
          </div>
        </div>

        {/* Text Block */}
        <div className="z-20 px-6 py-6 w-full lg:w-5/12">
          <div className="flex flex-col gap-6 lg:py-10 lg:pr-5">
            {/* Title */}
            <h2
              className="tracking-tight"
              id="title"
              style={{
                fontSize: `${title.styles.fontSize}px`,
                fontWeight: +title.styles.fontWeight,
                lineHeight: `${title.styles.lineHeight}px`,
                fontStyle: title.styles.isItalic ? 'italic' : 'normal',
                color:
                  hoveredElementId === 'title'
                    ? title.styles.onHoverColor
                    : title.styles.color,
              }}
              onMouseEnter={() => setHoveredElementId('title')}
              onMouseLeave={() => setHoveredElementId(undefined)}
            >
              {title.text}
            </h2>

            {/* Description */}
            <p
              id="description"
              style={{
                fontSize: `${description.styles.fontSize}px`,
                fontWeight: +description.styles.fontWeight,
                lineHeight: `${description.styles.lineHeight}px`,
                fontStyle: description.styles.isItalic ? 'italic' : 'normal',
                color:
                  hoveredElementId === 'description'
                    ? description.styles.onHoverColor
                    : description.styles.color,
              }}
              onMouseEnter={() => setHoveredElementId('description')}
              onMouseLeave={() => setHoveredElementId(undefined)}
            >
              {description.text}
            </p>

            {/* Features */}
            <div className="flex flex-col gap-2">
              {/* Features Title */}
              <p
                id="featuresTitle"
                style={{
                  fontSize: `${featuresTitle.styles.fontSize}px`,
                  fontWeight: +featuresTitle.styles.fontWeight,
                  lineHeight: `${featuresTitle.styles.lineHeight}px`,
                  fontStyle: featuresTitle.styles.isItalic
                    ? 'italic'
                    : 'normal',
                  color:
                    hoveredElementId === 'featuresTitle'
                      ? featuresTitle.styles.onHoverColor
                      : featuresTitle.styles.color,
                }}
                onMouseEnter={() => setHoveredElementId('featuresTitle')}
                onMouseLeave={() => setHoveredElementId(undefined)}
              >
                {featuresTitle.text}
              </p>

              {/* Features List */}
              <ul className="flex flex-col gap-2">
                {features.map((feature, i) => {
                  let IconComponent: FC<ICommonIconProps> | null =
                    homepageTemplatesIconsObj[feature.icon.name]

                  if (!IconComponent) {
                    IconComponent = null
                  }

                  return (
                    <li key={`feature-${i}`} className="py-2">
                      <p
                        id={`feature-${i}`}
                        className="inline-flex items-start"
                        style={{
                          fontSize: `${feature.text.styles.fontSize}px`,
                          fontWeight: +feature.text.styles.fontWeight,
                          lineHeight: `${feature.text.styles.lineHeight}px`,
                          fontStyle: feature.text.styles.isItalic
                            ? 'italic'
                            : 'normal',
                          color:
                            hoveredElementId === `feature-${i}`
                              ? feature.text.styles.onHoverColor
                              : feature.text.styles.color,
                        }}
                        onMouseEnter={() => setHoveredElementId(`feature-${i}`)}
                        onMouseLeave={() => setHoveredElementId(undefined)}
                      >
                        {IconComponent && (
                          <span className="inline-block mr-2 flex-shrink-0">
                            <IconComponent className="text-v8-violet" />
                          </span>
                        )}
                        {feature.text.text}
                      </p>
                    </li>
                  )
                })}
              </ul>
            </div>

            {/* Redirect Link */}
            <div className="max-sm:self-center pl-1">
              <Link
                id="link"
                href={link.href}
                target={link.openInNewWindow ? '_blank' : undefined}
                className="inline-flex rounded-md px-5 py-1.5 shadow-sm duration-300 shadow-md border"
                style={{
                  fontSize: `${link.styles.fontSize}px`,
                  fontWeight: +link.styles.fontWeight,
                  lineHeight: `${link.styles.lineHeight}px`,
                  fontStyle: link.styles.isItalic ? 'italic' : 'normal',
                  color:
                    hoveredElementId === 'link'
                      ? link.styles.onHoverColor
                      : link.styles.color,
                  backgroundColor:
                    hoveredElementId === 'link'
                      ? link.styles.onHoverBackgroundColor
                      : link.styles.backgroundColor,

                  borderColor:
                    hoveredElementId === 'link'
                      ? link.styles.onHoverBorderColor || undefined
                      : link.styles.borderColor || undefined,
                }}
                onMouseEnter={() => setHoveredElementId('link')}
                onMouseLeave={() => setHoveredElementId(undefined)}
              >
                {link.text}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
